import { IconX } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import type { CartItem } from "@/features/cart/types";
import { DietListItemPricing } from "@/features/diets/components/diet-list-item/diet-list-item-pricing";
import { DiscountCodes } from "@/features/diets/components/discount-codes";
import { OmnibusLowestPrice } from "@/features/diets/components/omnibus-lowest-price";
import {
  getCartItemOmnibusPresentation,
  getOmnibusDiscountCodes,
  isOmnibusEnabled,
} from "@/features/diets/helpers/omnibus";

import { getLocalePrice } from "@/lib/utilities";
import { cn } from "@/lib/utils";

export const CartDietFooter = ({ item }: { item: CartItem }) => {
  const t = useTranslations();
  const days = item.configurationItem.dates.length;

  // Omnibus (MPL-1835): under the flag the price is shown as reduced only when
  // its L is above the price without codes, or when a code applies; the
  // discount badges stay as they are.
  const omnibusEnabled = isOmnibusEnabled();
  const omnibus = getCartItemOmnibusPresentation({
    pricing: item.pricing,
    days,
    lowestPrice: item.lowestPriceLast30Days,
  });
  const hasCodes = Boolean(
    getOmnibusDiscountCodes(item.pricing.discounts)?.length,
  );
  const showLowestPrice = omnibusEnabled && omnibus.promotion;

  return (
    <div
      className={cn(
        "mt-4 flex flex-col flex-wrap gap-4 max-md:border-t max-md:pt-4 md:mt-auto md:flex-row md:items-center md:gap-6 xl:flex-nowrap",
        // The day price, badges and total share one baseline: centred boxes
        // drift apart by the font's regular/bold metrics (Firefox), and the
        // L line hangs below the price row.
        omnibusEnabled && "md:items-baseline",
      )}
    >
      <div className="order-2 flex w-full flex-col gap-1 rounded-lg bg-brand-grey1 p-3 md:order-1 md:w-auto md:rounded-none md:bg-transparent md:p-0 md:p-4">
        <div
          className={cn(
            "flex w-full items-center gap-1",
            omnibusEnabled && "items-baseline",
          )}
        >
          <p className="flex items-center gap-1 whitespace-nowrap">
            {t("common.days", { count: days })}
            <IconX className="h-2.5 w-2.5" stroke={2} />
          </p>
          {omnibusEnabled ? (
            <span
              className={cn(
                "whitespace-nowrap font-bold text-md",
                (omnibus.promotion || hasCodes) && "text-brand-red",
              )}
              data-pw="cart-item-day-price"
            >
              {getLocalePrice({
                price: item.pricing.final.gross / (days || 1),
              })}
            </span>
          ) : (
            <DietListItemPricing
              discountCodes={item.pricing.discounts}
              hideOriginalPrice={true}
              numberOfDays={days}
              price={item.pricing.before.gross / days}
              showTitle={false}
              totalPrice={item.pricing.before.gross}
              withDaysCount={true}
            />
          )}
          <div className="ml-auto whitespace-nowrap font-bold text-xl md:hidden">
            {getLocalePrice({ price: item.pricing.final.gross })}
          </div>
        </div>
        {showLowestPrice && (
          // On desktop the L line runs on under the badges in one line
          // instead of widening the price box, which would push them away.
          <OmnibusLowestPrice
            className="md:mt-1 md:whitespace-nowrap md:contain-inline-size"
            lowestPrice={omnibus.lowestPrice}
          />
        )}
      </div>

      {item.pricing.discounts && item.pricing.discounts.length > 0 && (
        <div className="order-1 flex flex-col items-start gap-1 md:order-2">
          <div
            className={cn(
              "flex gap-2",
              // A badge's baseline comes from its icon, ~4px below the text;
              // take it from the text instead.
              omnibusEnabled && "[&_p>span]:self-baseline",
            )}
          >
            <DiscountCodes discountCodes={item.pricing.discounts} />
          </div>
        </div>
      )}
      <div
        className={cn(
          "order-3 mt-auto ml-auto hidden whitespace-nowrap font-bold text-xl md:block",
          // Under the flag the total sits on the day price row (design).
          omnibusEnabled && "mt-0",
        )}
      >
        {getLocalePrice({ price: item.pricing.final.gross })}
      </div>
    </div>
  );
};
